import Loader from "../../../components/Loader";

const TransactionDashboard = ({ dashboardData, loading, isSidebarCollapsed }) => {

    if (loading) return <Loader />;

    // Fallback to mock if no real data
    const data = dashboardData || {
        current_loi_pipeline: { amount: 84240, deals: 8 },
        current_hopper: { amount: 519420, deals: 12 },
        expected_closings: {
            "30 Days": 291220,
            "60 Days": 291220,
            "90 Days": 291220,
        },
        hopper_split: {
            company: 259710,
            broker: 259710,
        },
        this_week: {
            loiAdded: { amount: 0, deals: 0 },
            hopperGain: { amount: 0, deals: 0 },
            closed: { amount: 0, deals: 0 },
            lost: { amount: 0, deals: 0 },
            netHopperChange: { amount: 0, deals: 0 },
        },
        deals: [],
        advisors: []
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
        }).format(amount || 0);
    };

    const {
        current_loi_pipeline,
        current_hopper,
        expected_closings,
        hopper_split,
        this_week = {},
        deals = [],
        advisors = []
    } = data;

    return (
        <div className="light-bg-L pb-3 px-4 py-4">
            <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} light-bg-L`}>

                {/* Header */}
                <div className="mb-3">
                    <p className="head-2 dark-H">Transaction Dashboard</p>
                </div>

                {/* Top Metrics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
                    {/* Current LOI Pipeline */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <p className="body-N text-gray-600">Current LOI Pipeline</p>
                        <p className="head-2 dark-H mt-2">
                            {formatCurrency(current_loi_pipeline?.amount)}
                        </p>
                        <p className="body-S text-gray-500 mt-1">
                            {current_loi_pipeline?.deals || 0} deals
                        </p>
                    </div>

                    {/* Current Hopper */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <p className="body-N text-gray-600">Current Hopper</p>
                        <p className="head-2 text-orange-600 mt-2">
                            {formatCurrency(current_hopper?.amount)}
                        </p>
                        <p className="body-S text-gray-500 mt-1">
                            {current_hopper?.deals || 0} deals
                        </p>
                    </div>

                    {/* Expected Closings */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <p className="body-N text-gray-600 mb-4">Expected Closings</p>
                        {expected_closings && Object.entries(expected_closings).map(([period, amount]) => (
                            <div key={period} className="flex justify-between py-2 border-b last:border-0">
                                <span className="body-S">{period}</span>
                                <span className="body-S font-semibold text-green-600">
                                    {formatCurrency(amount)}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Hopper Split */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <p className="body-N text-gray-600 mb-4">Hopper Split (50/50)</p>
                        <div className="space-y-4">
                            <div className="flex justify-between">
                                <span className="body-S">Company</span>
                                <span className="body-S font-medium">
                                    {formatCurrency(hopper_split?.company)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="body-S">Broker</span>
                                <span className="body-S font-medium">
                                    {formatCurrency(hopper_split?.broker)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* This Week Section */}
                <div className="mb-3">
                    <p className="head-4 dark-H mb-4">THIS WEEK</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {[
                            { label: "LOI Added", ...this_week.loiAdded },
                            { label: "Hopper Gain", ...this_week.hopperGain },
                            { label: "Closed", ...this_week.closed, color: "text-green-600" },
                            { label: "Lost", ...this_week.lost, color: "text-red-600" },
                            { label: "Net Hopper Change", ...this_week.netHopperChange, color: "text-green-600" },
                        ].map((item, i) => (
                            <div key={i} className="bg-white p-5 rounded-xl border border-gray-100">
                                <p className="body-N text-gray-600">{item.label}</p>
                                <p className={`head-4 mt-2 ${item.color || "dark-H"}`}>
                                    {formatCurrency(item.amount)}
                                </p>
                                <p className="body-S text-gray-500 mt-1">{item.deals} deals</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Deals and Advisor Leaderboard */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    {/* ==================== DEALS TABLE ==================== */}
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <p className="head-4 dark-H">Deals</p>
                            <button className="text-green-H body-S flex items-center gap-1 hover:underline">
                                Export ↓
                            </button>
                        </div>

                        <div className="bg-white rounded-xl border overflow-x-auto overflow-y-auto max-h-[500px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                            <table className="w-full min-w-[1100px]">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr className="text-left uppercase body-N dark-M">
                                        <th className="p-4 sticky left-0 bg-gray-50 z-20 ">
                                            Property Name
                                        </th>
                                        <th className="p-4">Advisor</th>
                                        <th className="p-4">Acquisition/Disposition</th>
                                        <th className="p-4">Status</th>
                                        <th className="p-4">Executed LOI Date</th>
                                        <th className="p-4">Contract Date</th>
                                        <th className="p-4">Closed Date</th>
                                        <th className="p-4">GCI to Company</th>
                                        <th className="p-4">Lost Date</th>
                                    </tr>
                                </thead>

                                <tbody className="body-N dark-M">
                                    {deals.length > 0 ? (
                                        deals.map((deal, idx) => (
                                            <tr key={idx} className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">
                                                    {deal.property_name || deal.name}
                                                </td>
                                                <td className="p-4">{deal.advisor}</td>
                                                <td className="p-4">{deal.side}</td>
                                                <td className={`p-4 font-medium
                                    ${deal.status === "Closed" ? "text-green-600" :
                                                        deal.status === "Lost" ? "text-red-600" :
                                                            deal.status === "LOI" ? "text-blue-600" :
                                                                "text-orange-500"}`}>
                                                    {deal.status}
                                                </td>
                                                <td className="p-4">{deal.loi_date}</td>
                                                <td className="p-4">{deal.uc_date}</td>
                                                <td className="p-4">{deal.exp_close_date}</td>
                                                <td className="p-4 text-green-600 font-medium">
                                                    {formatCurrency(deal.gci_to_company)}
                                                </td>
                                                <td className="p-4">
                                                    {formatCurrency(deal.contract_price)}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <>
                                            {/* Static Rows with Sticky First Column */}
                                            <tr className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">Dollar General - Madison, GA</td>
                                                <td className="p-4">Alec Marks</td>
                                                <td className="p-4">Buyer</td>
                                                <td className="p-4 text-orange-500 font-medium">Under Contract</td>
                                                <td className="p-4">05/01/2025</td>
                                                <td className="p-4">05/08/2025</td>
                                                <td className="p-4">06/15/2025</td>
                                                <td className="p-4 text-green-600 font-medium">$46,380</td>
                                                <td className="p-4">$2,319,000</td>
                                            </tr>

                                            <tr className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">PNC Bank - Missouri City, TX</td>
                                                <td className="p-4">Rutland Patterson</td>
                                                <td className="p-4">Buyer</td>
                                                <td className="p-4 text-orange-500 font-medium">Under Contract</td>
                                                <td className="p-4">04/15/2025</td>
                                                <td className="p-4">04/22/2025</td>
                                                <td className="p-4">05/20/2025</td>
                                                <td className="p-4 text-green-600 font-medium">$49,200</td>
                                                <td className="p-4">$2,460,000</td>
                                            </tr>

                                            <tr className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">DG - East Palestine, OH</td>
                                                <td className="p-4">Sam Roe</td>
                                                <td className="p-4">Buyer</td>
                                                <td className="p-4 text-blue-600 font-medium">LOI</td>
                                                <td className="p-4">05/10/2025</td>
                                                <td className="p-4">—</td>
                                                <td className="p-4">—</td>
                                                <td className="p-4 text-green-600 font-medium">$27,092</td>
                                                <td className="p-4">$1,354,600</td>
                                            </tr>

                                            <tr className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">McDonald's - Harlan, KY</td>
                                                <td className="p-4">Rutland Patterson</td>
                                                <td className="p-4">Buyer</td>
                                                <td className="p-4 text-green-600 font-medium">Closed</td>
                                                <td className="p-4">03/12/2025</td>
                                                <td className="p-4">03/19/2025</td>
                                                <td className="p-4">04/10/2025</td>
                                                <td className="p-4 text-green-600 font-medium">$52,000</td>
                                                <td className="p-4">$2,600,000</td>
                                            </tr>

                                            <tr className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">Family Dollar - Anderson, SC</td>
                                                <td className="p-4">Brandon Hanks</td>
                                                <td className="p-4">Buyer</td>
                                                <td className="p-4 text-orange-500 font-medium">Under Contract</td>
                                                <td className="p-4">04/18/2025</td>
                                                <td className="p-4">04/26/2025</td>
                                                <td className="p-4">05/30/2025</td>
                                                <td className="p-4 text-green-600 font-medium">$36,100</td>
                                                <td className="p-4">$1,805,000</td>
                                            </tr>

                                            <tr className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">Starbucks - Peoria, AZ</td>
                                                <td className="p-4">Shane Karnes</td>
                                                <td className="p-4">Buyer</td>
                                                <td className="p-4 text-blue-600 font-medium">LOI</td>
                                                <td className="p-4">05/14/2025</td>
                                                <td className="p-4">—</td>
                                                <td className="p-4">—</td>
                                                <td className="p-4 text-green-600 font-medium">$42,000</td>
                                                <td className="p-4">$2,100,000</td>
                                            </tr>

                                            <tr className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">Valvoline - Gibsonia, PA</td>
                                                <td className="p-4">Alec Marks</td>
                                                <td className="p-4">Buyer</td>
                                                <td className="p-4 text-red-600 font-medium">Lost</td>
                                                <td className="p-4">03/01/2025</td>
                                                <td className="p-4">—</td>
                                                <td className="p-4">—</td>
                                                <td className="p-4 text-green-600 font-medium">$22,000</td>
                                                <td className="p-4">$1,100,000</td>
                                            </tr>
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ==================== ADVISOR PIPELINE LEADERBOARD ==================== */}
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <p className="head-4 dark-H">Advisor Pipeline (Leaderboard)</p>
                            <button className="text-green-H body-S flex items-center gap-1 hover:underline">
                                Export ↓
                            </button>
                        </div>

                        <div className="bg-white rounded-xl border overflow-x-auto overflow-y-auto max-h-[500px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                            <table className="w-full min-w-[900px]">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr className="text-left uppercase body-N dark-M">
                                        <th className="p-4 sticky left-0 bg-gray-50 z-20 shadow-sm">
                                            Advisor
                                        </th>
                                        <th className="p-4">LOI Pipeline</th>
                                        <th className="p-4">Hopper (UC)</th>
                                        <th className="p-4">Next 90 Days</th>
                                        <th className="p-4">Closed YTD</th>
                                        <th className="p-4">GCI to Company<br />YTD</th>
                                    </tr>
                                </thead>

                                <tbody className="body-N dark-M">
                                    {advisors.length > 0 ? (
                                        advisors.map((advisor, idx) => (
                                            <tr key={idx} className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">
                                                    {advisor.name}
                                                </td>
                                                <td className="p-4 text-blue-600">{formatCurrency(advisor.loi_pipeline)}</td>
                                                <td className="p-4 text-orange-500">{formatCurrency(advisor.hopper)}</td>
                                                <td className="p-4 text-orange-500">{formatCurrency(advisor.next_90_days)}</td>
                                                <td className="p-4 text-green-600">{formatCurrency(advisor.closed_ytd)}</td>
                                                <td className="p-4">{formatCurrency(advisor.gci_company_ytd)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <>
                                            <tr className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">Sam Roe</td>
                                                <td className="p-4 text-blue-600">$36,720</td>
                                                <td className="p-4 text-orange-500">$0</td>
                                                <td className="p-4 text-orange-500">$0</td>
                                                <td className="p-4 text-green-600">$477,871</td>
                                                <td className="p-4">$31,250</td>
                                            </tr>

                                            <tr className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">Rutland Patterson</td>
                                                <td className="p-4 text-blue-600">$27,270</td>
                                                <td className="p-4 text-orange-500">$226,460</td>
                                                <td className="p-4 text-orange-500">$42,460</td>
                                                <td className="p-4 text-green-600">$290,104</td>
                                                <td className="p-4">$44,890</td>
                                            </tr>

                                            <tr className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">Alec Marks</td>
                                                <td className="p-4 text-blue-600">$20,250</td>
                                                <td className="p-4 text-orange-500">$217,560</td>
                                                <td className="p-4 text-orange-500">$191,760</td>
                                                <td className="p-4 text-green-600">$48,350</td>
                                                <td className="p-4">$15,320</td>
                                            </tr>

                                            <tr className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">Phil Hontzas</td>
                                                <td className="p-4 text-blue-600">$0</td>
                                                <td className="p-4 text-orange-500">$18,400</td>
                                                <td className="p-4 text-orange-500">$0</td>
                                                <td className="p-4 text-green-600">$0</td>
                                                <td className="p-4">$0</td>
                                            </tr>

                                            <tr className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">Shane Karnes</td>
                                                <td className="p-4 text-blue-600">$0</td>
                                                <td className="p-4 text-orange-500">$57,000</td>
                                                <td className="p-4 text-orange-500">$57,000</td>
                                                <td className="p-4 text-green-600">$0</td>
                                                <td className="p-4">$0</td>
                                            </tr>

                                            <tr className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">Josh Tharp</td>
                                                <td className="p-4 text-blue-600">$0</td>
                                                <td className="p-4 text-orange-500">$0</td>
                                                <td className="p-4 text-orange-500">$0</td>
                                                <td className="p-4 text-green-600">$0</td>
                                                <td className="p-4">$0</td>
                                            </tr>

                                            <tr className="border-t hover:bg-gray-50">
                                                <td className="p-4 sticky left-0 bg-white z-10 font-medium">Brandon Hanks</td>
                                                <td className="p-4 text-blue-600">$0</td>
                                                <td className="p-4 text-orange-500">$0</td>
                                                <td className="p-4 text-orange-500">$0</td>
                                                <td className="p-4 text-green-600">$0</td>
                                                <td className="p-4">$0</td>
                                            </tr>
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TransactionDashboard;